import { useCallback, useEffect, useRef, useState } from "react";
import type { DocumentRules } from "@/lib/brokerDocumentTypes";
import { messageFor } from "@/lib/errors";
import {
  documentRefusal,
  refusalMessage,
  screenFiles,
  uploadPercent,
} from "@/lib/fileUploads";

// Files on their way to PMS (BRK-S361's loss runs, BRK-S362's answers to a
// quote's subjectivities): one file a request, so each has its own row, its
// own progress and Stop, and its own refusal. A file PMS's served rules
// refuse never goes up; one PMS refuses (`DOCUMENT_REJECTED`) stays on its
// row in the words of the rule it broke; one that fails for any other reason
// stays with Retry. Nothing counts as up before PMS answers with it (NN #7):
// `onUploaded` hears each answer, and the row goes.

/** A file on its way up, or one that didn't make it. */
export interface FileUpload {
  key: string;
  file: File;
  state: "uploading" | "refused" | "failed";
  /** While uploading: how far, in whole percent. */
  percent: number;
  /** Refused or failed: why, in a sentence. */
  message: string | null;
}

/** Sends one file, reporting its progress and stopping on the signal. */
export type SendFile<T> = (
  file: File,
  options: {
    onProgress: (progress: { loaded: number; total: number }) => void;
    signal: AbortSignal;
  },
) => Promise<T>;

/** The rows with `row` in its key's place, or added after them. */
function put(rows: FileUpload[], row: FileUpload): FileUpload[] {
  return rows.some((r) => r.key === row.key)
    ? rows.map((r) => (r.key === row.key ? row : r))
    : [...rows, row];
}

export function useFileUploads<T>(
  sendFile: SendFile<T>,
  onUploaded: (result: T) => void,
) {
  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const latest = useRef(uploads);
  latest.current = uploads;
  const controllers = useRef(new Map<string, AbortController>());
  const rules = useRef<DocumentRules | null>(null);
  const keys = useRef(0);
  // Bumped by `clear`: an answer to a request from before changes nothing.
  const generation = useRef(0);
  const sendRef = useRef(sendFile);
  const doneRef = useRef(onUploaded);
  useEffect(() => {
    sendRef.current = sendFile;
    doneRef.current = onUploaded;
  });

  const start = useCallback((key: string, file: File) => {
    const generationAtStart = generation.current;
    const current = () => generation.current === generationAtStart;
    const controller = new AbortController();
    controllers.current.set(key, controller);
    setUploads((rows) =>
      put(rows, { key, file, state: "uploading", percent: 0, message: null }),
    );
    sendRef
      .current(file, {
        signal: controller.signal,
        onProgress: ({ loaded, total }) => {
          if (!current()) return;
          setUploads((rows) =>
            rows.map((r) =>
              r.key === key && r.state === "uploading"
                ? { ...r, percent: uploadPercent(loaded, total) }
                : r,
            ),
          );
        },
      })
      .then(
        (result) => {
          controllers.current.delete(key);
          if (!current()) return;
          setUploads((rows) => rows.filter((r) => r.key !== key));
          doneRef.current(result);
        },
        (err: unknown) => {
          controllers.current.delete(key);
          if (!current()) return;
          // Stopped: the broker's choice, not a failure to show.
          if (controller.signal.aborted) {
            setUploads((rows) => rows.filter((r) => r.key !== key));
            return;
          }
          const refusal = documentRefusal(err);
          setUploads((rows) =>
            rows.map((r) =>
              r.key !== key
                ? r
                : refusal
                  ? {
                      ...r,
                      state: "refused",
                      message: refusalMessage(refusal, rules.current),
                    }
                  : { ...r, state: "failed", message: messageFor(err) },
            ),
          );
        },
      );
  }, []);

  /** Sends the chosen files, after PMS's served rules pass them here,
   *  counting the `held` already on PMS's side and those on their way. */
  const send = useCallback(
    (chosen: readonly File[], served: DocumentRules, held: number) => {
      rules.current = served;
      const onTheirWay = latest.current.filter(
        (u) => u.state === "uploading",
      ).length;
      const { refused } = screenFiles(chosen, served, held + onTheirWay);
      const why = new Map(refused.map((r) => [r.file, r.message]));
      for (const file of chosen) {
        keys.current += 1;
        const key = `upload-${String(keys.current)}`;
        const message = why.get(file);
        if (message === undefined) start(key, file);
        else {
          setUploads((rows) =>
            put(rows, { key, file, state: "refused", percent: 0, message }),
          );
        }
      }
    },
    [start],
  );

  const stop = useCallback((key: string) => {
    controllers.current.get(key)?.abort();
  }, []);

  const retry = useCallback(
    (key: string) => {
      const row = latest.current.find(
        (u) => u.key === key && u.state === "failed",
      );
      if (row) start(key, row.file);
    },
    [start],
  );

  const dismiss = useCallback((key: string) => {
    setUploads((rows) => rows.filter((r) => r.key !== key));
  }, []);

  /** Starts over (another submission): what's on its way still lands on
   *  PMS, but its answer changes nothing here. */
  const clear = useCallback(() => {
    generation.current += 1;
    setUploads([]);
  }, []);

  return { uploads, send, stop, retry, dismiss, clear };
}
